import { BloctoConnector } from '@pancakeswap/wagmi/connectors/blocto'
import { TrustWalletConnector } from '@pancakeswap/wagmi/connectors/trustWallet'
import { CHAINS } from 'config/chains'
import { PUBLIC_NODES } from 'config/nodes'
import memoize from 'lodash/memoize'
import { configureChains, createConfig, createStorage } from 'wagmi'
import { mainnet } from 'wagmi/chains'
import { CoinbaseWalletConnector } from 'wagmi/connectors/coinbaseWallet'
import { InjectedConnector } from 'wagmi/connectors/injected'
import { LedgerConnector } from 'wagmi/connectors/ledger'
import { MetaMaskConnector } from 'wagmi/connectors/metaMask'
import { WalletConnectLegacyConnector } from 'wagmi/connectors/walletConnectLegacy'
import { jsonRpcProvider } from 'wagmi/providers/jsonRpc'
import { KastleWalletConnector } from '@pancakeswap/wagmi/connectors/kastleWallet'
import { KasKeeperConnector } from '@pancakeswap/wagmi/connectors/kasKeeper'
import { KasWareConnector } from '@pancakeswap/wagmi/connectors/kasWare'
// get most configs chain nodes length
const mostNodesConfig = Object.values(PUBLIC_NODES).reduce((prev, cur) => {
  return cur.length > prev ? cur.length : prev
}, 0)

export const { publicClient, chains } = configureChains(
  CHAINS,
  Array.from({ length: mostNodesConfig })
    .map((_, i) => i)
    .map((i) => {
      return jsonRpcProvider({
        rpc: (chain) => {
          if (process.env.NODE_ENV === 'test' && chain.id === mainnet.id && i === 0) {
            return { http: 'https://cloudflare-eth.com' }
          }
          return PUBLIC_NODES[chain.id]?.[i]
            ? {
                http: PUBLIC_NODES[chain.id][i],
              }
            : null
        },
      })
    }),
  {
    batch: {
      multicall: {
        batchSize: 1024 * 200,
      },
    },
  },
)

export const injectedConnector = new InjectedConnector({
  chains,
  options: {
    shimDisconnect: false,
  },
})

export const coinbaseConnector = new CoinbaseWalletConnector({
  chains,
  options: {
    appName: 'Kaspa Finance',
    appLogoUrl: 'https://app.kaspafinance.io/kaspaLogo1.svg',
  },
})

export const walletConnectConnector = new WalletConnectLegacyConnector({
  chains,
  options: {
    qrcode: true,
  },
})

export const walletConnectNoQrCodeConnector = new WalletConnectLegacyConnector({
  chains,
  options: {
    qrcode: false,
  },
})

export const metaMaskConnector = new MetaMaskConnector({
  chains,
  options: {
    // Prefer the real MetaMask provider in multi-injected environments.
    // Some extensions (e.g., KasWare) incorrectly set isMetaMask=true,
    // so we filter by EIP-6963 info and known flags.
    // @ts-expect-error: getProvider is not a standard MetaMaskConnector option, but we use it for custom provider selection.
    getProvider: () => {
      if (typeof window === 'undefined') return undefined as any
      const w = window as any

      // 1) EIP-6963: prefer provider with rdns/name matching MetaMask
      const details: any[] = w?.ethereum?.eip6963ProviderDetails || []
      const byEip6963 = details.find((d) => {
        const rdns = d?.info?.rdns?.toLowerCase?.()
        const name = d?.info?.name?.toLowerCase?.()
        return rdns === 'io.metamask' || name === 'metamask'
      })?.provider
      if (byEip6963) return byEip6963

      // 2) Multi-provider shim: choose provider that isMetaMask and not KasWare
      const providers: any[] = w?.ethereum?.providers || []
      const byProviders = providers.find((p) => p?.isMetaMask && !p?.isKasWare)
      if (byProviders) return byProviders

      // 3) Single provider window.ethereum: ensure not KasWare masquerading as MetaMask
      if (w?.ethereum?.isMetaMask && !w?.ethereum?.isKasWare) return w.ethereum

      return w?.ethereum
    },
    shimDisconnect: false,
  },
})

export const kastleWalletConnector = new KastleWalletConnector({
  chains,
  options: {
    shimDisconnect: true,
  },
})

export const kasKeeperConnector = new KasKeeperConnector({
  chains,
  options: {
    shimDisconnect: true,
  },
})

export const kasWareConnector = new KasWareConnector({
  chains,
  options: {
    shimDisconnect: true,
  },
})
export const trustWalletConnector = new TrustWalletConnector({
  chains,
  options: {
    shimDisconnect: false,
    shimChainChangedDisconnect: true,
  },
})

export const noopStorage = {
  getItem: (_key) => '',
  setItem: (_key, _value) => null,
  removeItem: (_key) => null,
}

export const wagmiConfig = createConfig({
  storage: createStorage({
    storage: typeof window !== 'undefined' ? window.localStorage : noopStorage,
    key: 'wagmi_v1',
  }),
  autoConnect: false,
  publicClient,
  connectors: [
    metaMaskConnector,
    injectedConnector,
    coinbaseConnector,
    walletConnectConnector,
    kastleWalletConnector,
    kasKeeperConnector,
    kasWareConnector,
    // @ts-ignore FIXME: wagmi
    trustWalletConnector,
  ],
})

export const CHAIN_IDS = chains.map((c) => c.id)

export const isChainSupported = memoize((chainId: number) => (CHAIN_IDS as number[]).includes(chainId))
export const isChainTestnet = memoize((chainId: number) => {
  const found = chains.find((c) => c.id === chainId)
  return found ? 'testnet' in found : false
})
